import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../lib/auth';
import { doctorAvailabilityAPI, appointmentsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Calendar as CalIcon, ChevronLeft, ChevronRight, Plus, X, Plane, Activity, User, Briefcase, MapPin } from 'lucide-react';
import TouchTimePicker from '../components/TouchTimePicker';

const CATEGORIES = [
  { value: 'ooo', label: 'Out of Office', icon: Plane, color: '#ef4444', bg: '#fef2f2' },
  { value: 'surgery', label: 'Surgery / OT', icon: Activity, color: '#dc2626', bg: '#fee2e2' },
  { value: 'personal', label: 'Personal', icon: User, color: '#7c3aed', bg: '#f3e8ff' },
  { value: 'conference', label: 'Conference / CME', icon: Briefcase, color: '#0891b2', bg: '#ecfeff' },
  { value: 'other', label: 'Other', icon: MapPin, color: '#64748b', bg: '#f1f5f9' },
];

const HOURS = Array.from({ length: 14 }, (_, i) => i + 8); // 8 AM → 9 PM
const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const getWeekStart = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
};

const DoctorCalendarPage = () => {
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(getWeekStart(new Date()));
  const [blocks, setBlocks] = useState([]);
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editBlock, setEditBlock] = useState(null);
  const [defaultDate, setDefaultDate] = useState(null);

  const doctorId = user?.id;
  const isDoctor = (user?.role_name || '').toLowerCase() === 'doctor' || (user?.role_code || '').toLowerCase() === 'doctor';

  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  }), [weekStart]);

  const load = useCallback(async () => {
    if (!doctorId) return;
    setLoading(true);
    try {
      const [availRes, apptRes] = await Promise.all([
        doctorAvailabilityAPI.get(doctorId).catch(() => ({ data: {} })),
        appointmentsAPI.list({ doctor_id: doctorId }).catch(() => ({ data: [] })),
      ]);
      setBlocks(availRes.data?.blocked_dates || []);
      setAppts(apptRes.data || []);
    } catch { toast.error('Failed to load calendar'); }
    setLoading(false);
  }, [doctorId]);

  useEffect(() => { load(); }, [load]);

  const navigate = (dir) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(d);
  };

  const today = () => setWeekStart(getWeekStart(new Date()));

  const openAddBlock = (date) => {
    setEditBlock(null);
    setDefaultDate(date.toISOString().split('T')[0]);
    setModalOpen(true);
  };

  const handleUnblock = async (block) => {
    if (!window.confirm(`Remove "${CATEGORIES.find(c => c.value === block.category)?.label || block.reason}" block on ${block.date}?`)) return;
    try {
      await doctorAvailabilityAPI.unblockDate(doctorId, block.id);
      toast.success('Block removed');
      load();
    } catch { toast.error('Failed to remove'); }
  };

  if (!isDoctor) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <CalIcon className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-800 mb-2">My Calendar</h2>
        <p className="text-slate-500 max-w-md">This page is available only to doctors. To view a doctor's calendar for booking, use the Book Appointment flow.</p>
      </div>
    );
  }

  // Cell helper: find block for date+hour
  const getBlocksForCell = (date, hour) => {
    const dateStr = date.toISOString().split('T')[0];
    return blocks.filter(b => {
      if (b.date !== dateStr) return false;
      if (b.full_day) return true;
      if (!b.start_time || !b.end_time) return false;
      const sh = parseInt(b.start_time.split(':')[0]);
      const eh = parseInt(b.end_time.split(':')[0]);
      return hour >= sh && hour < eh;
    });
  };

  const getApptsForCell = (date, hour) => {
    const dateStr = date.toISOString().split('T')[0];
    return appts.filter(a => {
      const adate = a.appointment_date || a.date;
      const atime = a.time_slot || a.time;
      if (adate !== dateStr) return false;
      if (!atime) return false;
      const sh = parseInt(atime.split(':')[0]);
      return sh === hour;
    });
  };

  return (
    <div className="space-y-5" data-testid="doctor-calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Calendar</h1>
          <p className="text-sm text-slate-500 mt-0.5">By default you are <span className="font-semibold text-emerald-600">Free</span>. Block time for OOO, Surgery, Personal time, etc.</p>
        </div>
        <Button onClick={() => openAddBlock(new Date())} className="bg-teal-600 hover:bg-teal-700" data-testid="add-block-btn">
          <Plus size={16} className="mr-1.5" /> Block Time
        </Button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map(c => (
          <div key={c.value} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: c.bg, color: c.color }}>
            <c.icon size={12} /> {c.label}
          </div>
        ))}
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
          <CalIcon size={12} /> Appointment
        </div>
      </div>

      {/* Week navigator */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="prev-week"><ChevronLeft size={14} /></Button>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-slate-700">
            {weekStart.toLocaleDateString('en', { month: 'long', day: 'numeric' })} –
            {' '}
            {new Date(weekStart.getTime() + 6 * 86400000).toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
          <Button variant="ghost" size="sm" onClick={today} className="text-xs">Today</Button>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate(1)} data-testid="next-week"><ChevronRight size={14} /></Button>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-slate-200 bg-slate-50/70">
          <div className="p-2 text-[10px] uppercase font-semibold text-slate-400 text-center">Time</div>
          {weekDates.map((d, i) => {
            const isToday = d.toDateString() === new Date().toDateString();
            return (
              <button key={i} onClick={() => openAddBlock(d)} className={`p-2 text-center border-l border-slate-200 hover:bg-teal-50/50 transition ${isToday ? 'bg-teal-50' : ''}`}>
                <div className="text-[10px] uppercase font-semibold text-slate-500">{DAYS_OF_WEEK[i]}</div>
                <div className={`text-lg font-bold mt-0.5 ${isToday ? 'text-teal-600' : 'text-slate-800'}`}>{d.getDate()}</div>
              </button>
            );
          })}
        </div>

        {/* Hour rows */}
        <div className="max-h-[600px] overflow-y-auto">
          {HOURS.map(h => (
            <div key={h} className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-slate-100 min-h-[56px]">
              <div className="p-2 text-xs text-slate-400 font-mono text-center border-r border-slate-100 flex items-start justify-center pt-2">
                {h.toString().padStart(2, '0')}:00
              </div>
              {weekDates.map((d, i) => {
                const cellBlocks = getBlocksForCell(d, h);
                const cellAppts = getApptsForCell(d, h);
                return (
                  <div key={i} className="border-l border-slate-100 p-1 relative group hover:bg-teal-50/30 transition cursor-pointer"
                    onClick={() => cellBlocks.length === 0 && openAddBlock(d)}>
                    {cellBlocks.map((b, bi) => {
                      const cat = CATEGORIES.find(c => c.value === b.category) || CATEGORIES[4];
                      const Icon = cat.icon;
                      return (
                        <div key={bi} className="rounded px-1.5 py-1 text-[10px] mb-0.5 flex items-center justify-between gap-1 cursor-pointer"
                          style={{ backgroundColor: cat.bg, color: cat.color, borderLeft: `3px solid ${cat.color}` }}
                          onClick={(e) => { e.stopPropagation(); handleUnblock(b); }}
                          data-testid={`block-${b.id}`}
                          title={`Click to remove · ${b.reason || cat.label}`}>
                          <div className="flex items-center gap-1 min-w-0">
                            <Icon size={10} className="flex-shrink-0" />
                            <span className="truncate font-semibold">{b.reason || cat.label}</span>
                          </div>
                          <X size={10} className="opacity-0 group-hover:opacity-100 flex-shrink-0" />
                        </div>
                      );
                    })}
                    {cellAppts.map((a, ai) => (
                      <div key={ai} className="rounded px-1.5 py-1 text-[10px] mb-0.5 bg-blue-50 text-blue-700 border-l-2 border-blue-500 truncate"
                        title={`${a.time_slot || a.time} · ${a.patient_name}`}>
                        {a.time_slot || a.time} {a.patient_name}
                      </div>
                    ))}
                    {!cellBlocks.length && !cellAppts.length && (
                      <Plus size={14} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-slate-300 opacity-0 group-hover:opacity-100" />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Block list */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><CalIcon size={14} /> Upcoming Time Blocks ({blocks.filter(b => b.date >= new Date().toISOString().split('T')[0]).length})</h4>
        {blocks.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No time blocks yet. Click any empty slot in the calendar above or use "Block Time" button.</p>
        ) : (
          <div className="space-y-1.5">
            {[...blocks].sort((a, b) => a.date.localeCompare(b.date)).slice(0, 20).map(b => {
              const cat = CATEGORIES.find(c => c.value === b.category) || CATEGORIES[4];
              const Icon = cat.icon;
              return (
                <div key={b.id} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: cat.bg }} data-testid={`block-list-${b.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon size={14} style={{ color: cat.color }} />
                    <span className="font-semibold text-slate-800 text-sm">{b.reason || cat.label}</span>
                    <span className="text-xs text-slate-500">· {b.date}</span>
                    {!b.full_day && <span className="text-xs text-slate-500">{b.start_time} - {b.end_time}</span>}
                    {b.full_day && <span className="text-xs text-slate-500">Full day</span>}
                  </div>
                  <button onClick={() => handleUnblock(b)} className="p-1 rounded hover:bg-white/50 text-red-500" data-testid={`remove-block-${b.id}`}>
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BlockModal open={modalOpen} onClose={() => setModalOpen(false)} doctorId={doctorId} defaultDate={defaultDate} editBlock={editBlock} onSaved={load} />
    </div>
  );
};

const BlockModal = ({ open, onClose, doctorId, defaultDate, editBlock, onSaved }) => {
  const [form, setForm] = useState({ date: '', category: 'ooo', reason: '', full_day: true, start_time: '09:00', end_time: '17:00', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        date: defaultDate || new Date().toISOString().split('T')[0],
        category: 'ooo', reason: '', full_day: true,
        start_time: '09:00', end_time: '17:00', notes: '',
      });
    }
  }, [open, defaultDate]);

  const save = async () => {
    if (!form.date) { toast.error('Pick a date'); return; }
    if (!form.full_day && form.start_time >= form.end_time) { toast.error('End time must be after start'); return; }
    setSaving(true);
    try {
      const cat = CATEGORIES.find(c => c.value === form.category);
      await doctorAvailabilityAPI.blockDate(doctorId, {
        date: form.date,
        category: form.category,
        reason: form.reason || cat?.label,
        full_day: form.full_day,
        start_time: form.full_day ? null : form.start_time,
        end_time: form.full_day ? null : form.end_time,
        notes: form.notes,
      });
      toast.success('Time blocked');
      onSaved(); onClose();
    } catch { toast.error('Failed to block'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Block Time</DialogTitle>
          <DialogDescription>Mark yourself as unavailable for a date or specific time range.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-sm font-medium">Date *</Label>
            <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="h-11 mt-1.5" data-testid="block-date-input" />
          </div>

          <div>
            <Label className="text-sm font-medium">Category</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              {CATEGORIES.map(c => {
                const Icon = c.icon;
                const active = form.category === c.value;
                return (
                  <button key={c.value} type="button" onClick={() => setForm({ ...form, category: c.value })}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition ${
                      active ? 'border-current shadow-sm scale-[1.02]' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    style={{ backgroundColor: active ? c.bg : 'white', color: active ? c.color : '#475569' }}
                    data-testid={`category-${c.value}`}>
                    <Icon size={14} /> {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium">Title (optional)</Label>
            <Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Family vacation, Cardiac surgery..." className="mt-1.5" data-testid="block-reason-input" />
          </div>

          <div className="flex items-center justify-between py-2 border-y border-slate-100">
            <Label className="text-sm font-medium m-0">Full Day</Label>
            <Switch checked={form.full_day} onCheckedChange={v => setForm({ ...form, full_day: v })} data-testid="full-day-switch" />
          </div>

          {!form.full_day && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">From</Label>
                <TouchTimePicker value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} testId="block-start-time" />
              </div>
              <div>
                <Label className="text-xs text-slate-600">To</Label>
                <TouchTimePicker value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} testId="block-end-time" />
              </div>
            </div>
          )}

          <div>
            <Label className="text-sm font-medium">Notes (optional)</Label>
            <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Internal note for Front Desk..." />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-block-btn">
              {saving ? 'Saving...' : 'Block Time'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DoctorCalendarPage;
